import { useEffect, useRef } from 'react';
import usePageSEO from '../hooks/usePageSEO';
import markup from './promoOfferMarkup';
import './PromoOfferPage.css';

const CAL_URL = 'https://cal.com/muhammad-furqan-karim-kidwai-uvsohz/30min';

const PromoOfferPage = () => {
  const rootRef = useRef(null);

  usePageSEO({
    title: 'Plouton AI | The One-Task Demo',
    description: 'Bring your most painful reconciliation. Watch a Plouton AI agent run it live in your portal, fully recorded. Keep the recording and the exceptions list.',
    path: '/promo-offer',
  });

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const cleanups = [];
    const bar = root.querySelector('#stickyCta');
    const card = root.querySelector('#book');
    const form = root.querySelector('#lead');

    if (bar && card && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        bar.classList.toggle('show', !entries[0].isIntersecting);
      }, { threshold: 0.05 });
      observer.observe(card);
      cleanups.push(() => observer.disconnect());
    }

    if (form && card) {
      const steps = root.querySelectorAll('#progress li:not(.rule)');
      const requiredFields = form.querySelectorAll('[data-req]');

      const setStep = (two) => {
        steps[0].classList.toggle('on', !two);
        steps[0].classList.toggle('complete', two);
        steps[1].classList.toggle('on', two);
        steps[0].removeAttribute('aria-current');
        steps[1].removeAttribute('aria-current');
        steps[two ? 1 : 0].setAttribute('aria-current', 'step');
      };

      const check = (el) => {
        const value = el.value.trim();
        let ok = value !== '';
        let message = el.getAttribute('data-empty');
        if (ok && el.type === 'email') {
          ok = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(value);
          message = el.getAttribute('data-bad');
        }
        const field = el.closest('.field');
        field.classList.toggle('bad', !ok);
        el.setAttribute('aria-invalid', ok ? 'false' : 'true');
        field.querySelector('.err').textContent = ok ? '' : message;
        return ok;
      };

      const onBlur = (event) => {
        const el = event.target;
        if (el.value.trim() !== '') check(el);
      };
      const onInput = (event) => {
        const el = event.target;
        if (el.closest('.field').classList.contains('bad')) check(el);
      };

      requiredFields.forEach((el) => {
        el.addEventListener('blur', onBlur);
        el.addEventListener('input', onInput);
        el.addEventListener('change', onInput);
      });

      const onSubmit = (event) => {
        event.preventDefault();
        let first = null;
        requiredFields.forEach((el) => {
          if (!check(el) && !first) first = el;
        });
        if (first) {
          first.focus();
          return;
        }

        const data = {};
        new FormData(form).forEach((value, key) => {
          data[key] = value;
        });
        data.email_domain = data.email.trim().split('@')[1].toLowerCase();
        data.task_words = data.task.trim() ? data.task.trim().split(/\s+/).length : 0;

        const notes = ['System: ' + data.system, data.task.trim() ? 'Task: ' + data.task.trim() : '']
          .filter(Boolean)
          .join('\n')
          .slice(0, 800);
        const direct = new URL(CAL_URL);
        direct.searchParams.set('name', data.name.trim());
        direct.searchParams.set('email', data.email.trim());
        direct.searchParams.set('notes', notes);
        const embedded = new URL(direct.toString());
        embedded.searchParams.set('embed', 'true');
        embedded.searchParams.set('layout', 'month_view');
        embedded.searchParams.set('theme', 'light');

        root.querySelector('#cal').src = embedded.toString();
        root.querySelector('#calAlt').href = direct.toString();
        root.querySelector('#forName').textContent = data.name.trim();
        root.querySelector('#forEmail').textContent = data.email.trim();

        card.classList.add('sent');
        setStep(true);
        card.scrollIntoView({ behavior: 'smooth', block: 'start' });
        root.querySelector('#doneTitle').focus({ preventScroll: true });
      };

      const onEdit = () => {
        card.classList.remove('sent');
        setStep(false);
        root.querySelector('#name').focus();
      };

      form.addEventListener('submit', onSubmit);
      const editButton = root.querySelector('#editDetails');
      editButton.addEventListener('click', onEdit);

      cleanups.push(() => {
        requiredFields.forEach((el) => {
          el.removeEventListener('blur', onBlur);
          el.removeEventListener('input', onInput);
          el.removeEventListener('change', onInput);
        });
        form.removeEventListener('submit', onSubmit);
        editButton.removeEventListener('click', onEdit);
      });
    }

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, []);

  return (
    <div
      className="promo-offer"
      ref={rootRef}
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
};

export default PromoOfferPage;
